import React, { FC, Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { formatDate } from 'utils/format';
import { Print } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    IconButton,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';

import { useTranslation } from 'react-i18next';
import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { AlertColor } from '@mui/material';

const PAGE_SIZE = 20;
import { waybillsTemplate } from 'apollo/templates/transport';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { NShiftShipmentWaybill } from 'models/nshift-shipment-waybill';
import {
    deleteWaybillTemplate,
    printWaybillsTemplate,
    printWaybillTemplate,
} from 'apollo/templates/transport/mutation.graphql';
import { downloadUrl } from 'utils/file';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { socketIo } from 'context/socket';
import { ErrorMessage } from 'formik';

const PrintWaybills: FC = () => {
    const { t: translate } = useTranslation(['common', 'transport', 'orders']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const socket = useContext(socketIo.getSocketContext());

    const [waybills, setWaybills] = useState<NShiftShipmentWaybill[]>([]);
    const [filters, setFilters] = useState<{ status: string }>({ status: 'SHOW_NOT_PRINTED' });
    const [isPrinting, setIsPrinting] = useState(false);
    const [printingWaybillId, setPrintingWaybillId] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const isFetchingMoreRef = useRef(false);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const { loading, fetchMore, refetch } = useQuery(waybillsTemplate, {
        fetchPolicy: 'network-only',
        variables: { filters, limit: PAGE_SIZE, offset: 0 },
        onError: (error) => {
            showBoundary(error);
        },
        onCompleted: ({ waybills: fetchedWaybills }) => {
            setWaybills(fetchedWaybills);
            setHasMore(fetchedWaybills.length === PAGE_SIZE);
        },
    });

    const loadMore = useCallback(async () => {
        if (isFetchingMoreRef.current || !hasMore) return;
        isFetchingMoreRef.current = true;
        try {
            const { data } = await fetchMore({
                variables: { filters, limit: PAGE_SIZE, offset: waybills.length },
            });
            const nextPage: NShiftShipmentWaybill[] = data?.waybills ?? [];
            setWaybills((current) => [...current, ...nextPage]);
            setHasMore(nextPage.length === PAGE_SIZE);
        } catch (error) {
            showBoundary(error);
        } finally {
            isFetchingMoreRef.current = false;
        }
    }, [hasMore, waybills.length, filters, fetchMore, showBoundary]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) loadMore();
            },
            { threshold: 0.1 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMore]);

    useEffect(() => {
        socket.on(
            'WAYBILLS_READY',
            ({
                data,
            }: {
                data: { waybills: NShiftShipmentWaybill[]; mergedPdfUrl: string; errorMessage?: string };
            }) => {
                setIsPrinting(false);
                refetch().then(({ data: refetchedData }) => {
                    if (refetchedData?.waybills) {
                        setWaybills(refetchedData.waybills);
                        setHasMore(refetchedData.waybills.length === PAGE_SIZE);
                    }
                });
                if (data.mergedPdfUrl) {
                    downloadUrl('merged-waybills', data.mergedPdfUrl);
                }
                if (data.errorMessage) {
                    handleErrors(data.errorMessage, 'warning', translate('transport:SOME_WAYBILLS'));
                }
            },
        );

        socket.on('WAYBILLS_ERROR', ({ data }: { data: { errorMessage: string } }) => {
            setIsPrinting(false);
            refetch();
            const errorMessage = data.errorMessage || '';
            handleErrors(errorMessage, 'error');
        });

        return () => {
            socket.removeAllListeners('WAYBILLS_READY');
            socket.removeAllListeners('WAYBILLS_ERROR');
        };
    }, [socket, refetch]);

    const handleErrors = (errorMessage: string, type: AlertColor, message?: string) => {
        // Show the raw nShift message(s). We don't map individual nShift messageCodes,
        // so whatever the carrier API reports is surfaced verbatim (English only).
        let displayMessage = errorMessage;
        if (errorMessage.includes('NShift API failed:')) {
            const errorJsonStr = errorMessage.split('NShift API failed:')[1];
            try {
                const errorObjects: { message: string }[] = JSON.parse(errorJsonStr);
                displayMessage = errorObjects.map((errorObject) => errorObject.message).join(' ');
            } catch {
                displayMessage = errorJsonStr.trim();
            }
        }
        setAppToasterContent({
            isOpen: true,
            severity: type,
            message: `${displayMessage}${message ? ' ' + message : ''}`,
        });
    };

    const [printWaybill] = useMutation(printWaybillTemplate, {
        onError: (error) => {
            setPrintingWaybillId(null);
            if (error.message.includes('ZipCodeInvalid')) {
                console.error(error);
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_ZIP_CODE_INVALID'),
                });
                return;
            }
            if (error.message.includes('Missing waybill print format')) {
                console.error(error);
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_MISSING_PRINT_FORMAT'),
                });
                return;
            }
            if (error.message.includes('Requested product(s) not available')) {
                console.error(error);
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_SERVICE_UNAVAILABLE'),
                });
                return;
            }
            if (error.message.includes('NShift API failed:')) {
                const errorJsonStr = error.message.split('NShift API failed:')[1];
                const errorObject = JSON.parse(errorJsonStr);
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: `${errorObject[0].field}: ${errorObject[0].message}`,
                });
                console.error(errorObject);
                return;
            }

            showBoundary(error);
        },
        onCompleted: ({ printWaybill: printedWaybill }) => {
            setPrintingWaybillId(null);
            const updatedWaybills = [...waybills];
            const waybillIndex = updatedWaybills.findIndex((waybill) => waybill._id === printedWaybill._id);
            if (waybillIndex > -1) {
                updatedWaybills.splice(waybillIndex, 1, { ...updatedWaybills[waybillIndex], ...printedWaybill });
                setWaybills(updatedWaybills);
                for (const file of (printedWaybill as NShiftShipmentWaybill).prints) {
                    downloadUrl(file.id, file.fileUrl);
                }
            }
        },
    });

    const [printWaybills] = useMutation(printWaybillsTemplate, {
        onError: (error) => {
            setIsPrinting(false);
            if (error.message.includes('ZipCodeInvalid')) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_ZIP_CODE_INVALID'),
                });
                return;
            }
            if (error.message.includes('Missing waybill print format')) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_MISSING_PRINT_FORMAT'),
                });
                return;
            }
            if (error.message.includes('Requested product(s) not available')) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_SERVICE_UNAVAILABLE'),
                });
                return;
            }
            showBoundary(error);
        },
        onCompleted: () => {
            // Result delivered via WAYBILLS_READY socket event
        },
    });

    const printWBill = (waybillId: string) => {
        setPrintingWaybillId(waybillId);
        printWaybill({ variables: { waybillId } });
    };

    const removeWaybill = (waybillId: string) => {
        deleteWaybill({ variables: { waybillId } });
    };

    const createAllWaybills = () => {
        setIsPrinting(true);
        printWaybills();
    };

    const [deleteWaybill] = useMutation(deleteWaybillTemplate, {
        onError: (error) => {
            showBoundary(error);
        },
        onCompleted: ({ deleteWaybill: deletedWaybillId }) => {
            const updatedWaybills = [...waybills];
            const waybillIndex = updatedWaybills.findIndex((waybill) => waybill._id === deletedWaybillId);
            if (waybillIndex > -1) {
                updatedWaybills.splice(waybillIndex, 1);
                setWaybills(updatedWaybills);
            }
        },
        update(cache: ApolloCache<NShiftShipmentWaybill[]>, { data: { deleteWaybill: deletedWaybillId } }) {
            cache.modify({
                fields: {
                    waybills(existingWaybillRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingWaybillRefs instanceof Array) {
                            return [...existingWaybillRefs].filter((waybillRef: Reference | undefined) => {
                                return deletedWaybillId._id !== readField('_id', waybillRef);
                            });
                        }
                        return [];
                    },
                },
            });
        },
    });

    const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
        const status = event.target.value;
        setWaybills([]);
        setHasMore(true);
        setFilters({ ...filters, status });
    };

    if (loading) {
        return <Loader />;
    }

    if (isPrinting) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ width: '100%', height: 200, gap: 2 }}
            >
                <Box sx={{ width: '100%' }}>
                    <LinearProgress color="secondary" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {translate('transport:CREATING_WAYBILLS_PLEASE_WAIT')}
                </Typography>
            </Box>
        );
    }

    return (
        <Fragment>
            <Box display="flex" justifyItems="center" mb={1}>
                <Typography variant="h2" sx={{ marginBottom: 1 }}>
                    {translate('transport:WAYBILLS')}
                </Typography>
            </Box>
            <Box display="flex" justifyItems="center" mb={1}>
                <Box flexGrow={1}>
                    <Select
                        value={filters.status}
                        labelId="waybill-filter"
                        sx={{ width: 300 }}
                        onChange={handleStatusFilterChange}
                    >
                        <MenuItem value="SHOW_NOT_PRINTED">{translate('transport:SHOW_NOT_PRINTED')}</MenuItem>
                        <MenuItem value="SHOW_PRINTED">{translate('transport:SHOW_PRINTED')}</MenuItem>
                    </Select>
                </Box>
                <LoadingButton loadingPosition="start" startIcon={<Print />} onClick={createAllWaybills}>
                    {translate('transport:CREATE_ALL_WAYBILLS')}
                </LoadingButton>
            </Box>
            <Table component={Paper}>
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('common:CLINIC')}</TableCell>
                        <TableCell>{translate('common:CREATED')}</TableCell>
                        <TableCell>{translate('orders:ORDERS')}</TableCell>
                        <TableCell sx={{ width: 240, minWidth: 240, maxWidth: 240 }}>
                            {translate('transport:WAYBILL')}
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {waybills?.map((waybill) => (
                        <TableRow key={waybill._id}>
                            <TableCell>{waybill.receiverReference?.name}</TableCell>
                            <TableCell>
                                {formatDate(new Date(waybill.createdAtNShift || waybill.createdAt), 'date')}
                            </TableCell>
                            <TableCell>
                                {waybill.orders.map((order) => (
                                    <Link
                                        key={order?._id}
                                        target="_blank"
                                        href={`/order-details/${order?._id}/main-view`}
                                        sx={{ marginRight: 1 }}
                                    >
                                        {order?.orderId}
                                    </Link>
                                ))}
                            </TableCell>

                            <TableCell sx={{ width: 240, minWidth: 240, maxWidth: 240 }}>
                                <Box sx={{ minHeight: 48, display: 'flex', alignItems: 'center' }}>
                                    {waybill.status === 'PRINTED' ? (
                                        (waybill.prints || []).map((print) => (
                                            <Link key={print.id} href={print.fileUrl}>
                                                {print.id}.{print.type}
                                            </Link>
                                        ))
                                    ) : printingWaybillId === waybill._id ? (
                                        <Typography variant="caption">
                                            {translate('transport:CREATING_WAYBILL')}
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="grey.500">
                                            {translate('transport:NOT_PRINTED')}
                                        </Typography>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                {waybill.status === 'PRINTED' ? (
                                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                                        <CheckCircleIcon color="success" fontSize="small" sx={{ marginRight: 0.5 }} />
                                        <Typography color="success.main" variant="body2">
                                            {translate('transport:PRINTED')}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <LoadingButton
                                        loading={printingWaybillId === waybill._id}
                                        disabled={printingWaybillId !== null}
                                        onClick={() => printWBill(waybill._id)}
                                    >
                                        {translate('common:CREATE')}
                                    </LoadingButton>
                                )}
                            </TableCell>
                            <TableCell onClick={() => removeWaybill(waybill._id)}>
                                <Tooltip title={translate('common:DELETE').toString()} placement="right">
                                    <IconButton sx={{ padding: 0 }} disabled={printingWaybillId !== null}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Box ref={sentinelRef} sx={{ height: 20 }} />
        </Fragment>
    );
};

export default PrintWaybills;
